import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Icon } from './icons';
import { ShareButton } from './share-card';
import { BookV2 } from '../types';
import { unitMeta } from './units';

/* ─────────────────────────────────────────────────────────────
   Tutorial Overlay
   ───────────────────────────────────────────────────────────── */

export const TUTORIAL_STEPS = [
  { target: 'today-ring',    tab: 'home', title: 'Seu hábito do dia',    body: 'Este anel mostra quantas páginas você leu hoje e quanto falta para bater a meta diária.', placement: 'bottom' },
  { target: 'streak',        tab: 'home', title: 'A chama acesa',        body: 'Dias consecutivos de leitura. Leia qualquer página e a chama continua viva.', placement: 'bottom' },
  { target: 'continue-card', tab: 'home', title: 'Continue lendo',       body: 'Toque no livro para ver detalhes. Toque em "Registrar leitura" para anotar as páginas direto daqui.', placement: 'top' },
  { target: 'tab-clubs',     tab: 'home', title: 'Clubes e grupos',      body: 'Leia em comunidade — clubes e grupos com ranking e estudo do livro coletivo. Sua biblioteca pessoal segue acessível pelo perfil ("Eu" → Minha estante).', placement: 'top' },
  { target: 'tab-add',       tab: 'home', title: 'Adicionar livro',      body: 'Toque no botão central para adicionar um livro — busque pelo nome, escaneie o código de barras ou cadastre manualmente.', placement: 'top' },
  { target: 'tab-me',        tab: 'home', title: 'Você',                 body: 'Perfil, notificações e ajustes. É aqui também que você pode rever este tutorial.', placement: 'top' },
];

export const TutorialOverlay: React.FC<{
  open: boolean;
  onFinish: () => void;
  onSkip: () => void;
  onRequestTab: (tab: string) => void;
}> = ({ open, onFinish, onSkip, onRequestTab }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: 400, h: 800 });
  // Altura REAL medida do card explicativo (apos render). Comeca com estimativa
  // conservadora; sera atualizada pelo useLayoutEffect que mede o ref.
  const [cardActualH, setCardActualH] = useState(220);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const current = TUTORIAL_STEPS[step];

  useEffect(() => {
    if (!open || !current) return;
    onRequestTab(current.tab);
  }, [open, step]); // eslint-disable-line

  // Bloqueia scroll do body enquanto o tutorial estiver aberto.
  // Sem isso, o usuario rola a pagina e o highlight (em fixed) descola
  // visualmente do card-alvo. Restaura ao fechar.
  useEffect(() => {
    if (!open) return;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      bodyTouchAction: document.body.style.touchAction,
    };
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.touchAction = prev.bodyTouchAction;
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !current) return;
    let cancelled = false;

    // Scroll inteligente baseado no placement do card explicativo:
    // - placement 'bottom' (card explicativo VEM ABAIXO) → highlight na PARTE SUPERIOR
    //   da viewport, deixando ~60% do espaco abaixo livre pra card explicativo.
    // - placement 'top' (card explicativo VEM ACIMA) → highlight na PARTE INFERIOR,
    //   deixando ~60% do espaco acima livre pra card explicativo.
    // Fixed elements (tabs) nao sao afetados por scroll, entao pulamos se for tab.
    const ensureVisible = () => {
      const target = document.querySelector(`[data-tour="${current.target}"]`) as HTMLElement | null;
      if (!target) return;
      const isFixed = String(current.target).startsWith('tab-');
      if (isFixed) return; // tabs nao precisam de scroll, ja estao visiveis

      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight;
      const safeTop = 70;
      const safeBottom = 100;

      // Posicao ideal do TOPO do target dentro do viewport
      let idealTop: number;
      if (current.placement === 'bottom') {
        // Card explicativo vai abaixo → highlight no terco superior
        idealTop = safeTop + 24;
      } else {
        // 'top' default — card explicativo acima → highlight no terco inferior
        // Coloca a BASE do target em ~75% da altura da viewport
        idealTop = Math.max(safeTop, vh * 0.72 - rect.height);
      }

      const scrollDelta = rect.top - idealTop;
      if (Math.abs(scrollDelta) < 8) return; // ja esta proximo, nao mexe

      // Reabilita scroll temporariamente, faz scroll suave, re-bloqueia
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      try {
        window.scrollBy({ top: scrollDelta, behavior: 'smooth' });
      } catch {
        window.scrollBy(0, scrollDelta);
      }
      // Re-bloqueia depois da animacao terminar (CSS smooth scroll dura ~300-400ms)
      setTimeout(() => {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }, 450);
    };

    const measure = () => {
      if (cancelled) return;
      const target = document.querySelector(`[data-tour="${current.target}"]`) as HTMLElement | null;
      if (!target) { setRect(null); return; }
      // Coordenadas RELATIVAS AO VIEWPORT — getBoundingClientRect ja faz isso.
      // Como o overlay esta em position:fixed (viewport), nenhum offset extra.
      const t = target.getBoundingClientRect();
      setRect({ x: t.left, y: t.top, w: t.width, h: t.height });
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };

    ensureVisible();
    // Mede em cascata pra capturar a posicao APOS o smooth scroll terminar.
    // Smooth scroll do browser leva ~300-400ms; mede repetidas vezes pra
    // garantir que pega o rect estabilizado.
    requestAnimationFrame(measure);
    const id1 = setTimeout(measure, 80);
    const id2 = setTimeout(measure, 200);
    const id3 = setTimeout(measure, 380);
    const id4 = setTimeout(measure, 520);

    // Re-mede no resize (rotacao de tela, teclado iOS)
    window.addEventListener('resize', measure);
    return () => {
      cancelled = true;
      clearTimeout(id1); clearTimeout(id2); clearTimeout(id3); clearTimeout(id4);
      window.removeEventListener('resize', measure);
    };
  }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mede altura REAL do card explicativo apos render. Como o conteudo do
  // card varia por step (titulos/bodies de tamanhos diferentes), preciso da
  // altura real pra calcular cardTop sem sobrar/faltar espaco. Sem isso,
  // o calculo usa o max teorico (380) e posiciona errado.
  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const measureCard = () => {
      if (!cardRef.current) return;
      const h = cardRef.current.scrollHeight;
      if (h > 0 && Math.abs(h - cardActualH) > 4) {
        setCardActualH(h);
      }
    };
    requestAnimationFrame(measureCard);
    const id1 = setTimeout(measureCard, 80);
    const id2 = setTimeout(measureCard, 250);
    return () => { clearTimeout(id1); clearTimeout(id2); };
  }, [open, step, current?.title, current?.body, cardActualH]);

  // Se o alvo deste passo NÃO existe nesta conta/tela (ex.: "Continue lendo"
  // numa home vazia no 1º login, ou uma aba que foi removida da navegação),
  // pula pro próximo em vez de desenhar um cartão boiando apontando pro vazio.
  // Vale TAMBÉM pra tab-* (não presumimos mais que toda aba existe): as abas
  // reais (tab-home/clubs/add/explore/me) são renderizadas na hora, então passam
  // no check; uma aba inexistente (ex.: tab-stats antigo) é pulada graciosamente.
  useEffect(() => {
    if (!open || !current) return;
    const t = setTimeout(() => {
      if (!document.querySelector(`[data-tour="${current.target}"]`)) {
        if (step >= TUTORIAL_STEPS.length - 1) onFinish();
        else setStep(step + 1);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [open, step, current]);

  if (!open || !current) return null;

  const next = () => {
    if (step >= TUTORIAL_STEPS.length - 1) onFinish();
    else setStep(step + 1);
  };
  const back = () => step > 0 && setStep(step - 1);

  const pad = 10;
  const hole = rect ? { x: rect.x - pad, y: rect.y - pad, w: rect.w + pad * 2, h: rect.h + pad * 2, r: 18 } : null;

  // Bug #11 fix: cardH fixo era 200 mas conteúdo pode passar 280 facilmente.
  // Botão 'Próximo' saía da viewport em iPhones menores. Agora usa vh dinâmico.
  const safeBottom = 100; // safe area + tabbar
  const safeTop = 70;
  const maxCardH = Math.min(380, viewport.h - safeTop - safeBottom);
  // Usa altura REAL medida (cardActualH) clampada ao max possivel. Sem isso,
  // o calculo de cardTop usaria 380 sempre e decidiria mal o placement.
  const cardH = Math.min(cardActualH, maxCardH);
  const cardW = Math.min(320, viewport.w - 40);
  let cardTop: number;
  if (!hole) cardTop = viewport.h / 2 - cardH / 2;
  else if (current.placement === 'top') {
    cardTop = hole.y - cardH - 16;
    if (cardTop < safeTop) cardTop = hole.y + hole.h + 16;
  } else {
    cardTop = hole.y + hole.h + 16;
    if (cardTop + cardH > viewport.h - safeBottom) cardTop = hole.y - cardH - 16;
  }
  // Garantia final — sempre cabe na viewport
  cardTop = Math.max(safeTop, Math.min(cardTop, viewport.h - cardH - safeBottom));
  const cardLeft = (viewport.w - cardW) / 2;
  const arrowAbove = hole && cardTop > hole.y + hole.h;
  const totalSteps = TUTORIAL_STEPS.length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'auto', maxWidth: 480, margin: '0 auto' }}>
      <svg width={viewport.w} height={viewport.h} style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}>
        <defs>
          <mask id="tut-mask">
            <rect width="100%" height="100%" fill="white"/>
            {hole && <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={hole.r} ry={hole.r} fill="black"/>}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(10,8,7,0.78)" mask="url(#tut-mask)"/>
        {hole && (
          <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={hole.r} ry={hole.r}
            fill="none" stroke="var(--ember)" strokeWidth="2"
            style={{ filter: 'drop-shadow(0 0 12px rgba(199,83,26,0.5))' }}>
            <animate attributeName="stroke-opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite"/>
          </rect>
        )}
      </svg>

      <button onClick={onSkip} style={{
        position: 'absolute', top: 40, right: 20,
        padding: '8px 16px',
        background: 'rgba(245, 241, 234, 0.95)', color: '#1A1614',
        fontSize: 13, fontWeight: 500,
        borderRadius: 999, border: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      }}>Pular tutorial</button>

      <div ref={cardRef} style={{
        position: 'absolute',
        top: cardTop,
        left: cardLeft, width: cardW,
        maxHeight: maxCardH,
        overflowY: 'auto',
        background: 'var(--paper)', color: 'var(--ink)',
        borderRadius: 22, padding: 20,
        boxShadow: '0 24px 48px -12px rgba(0,0,0,0.5)',
        transition: 'top 400ms cubic-bezier(0.2, 0.8, 0.2, 1), left 400ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        boxSizing: 'border-box',
      }}>
        {hole && (
          <div style={{
            position: 'absolute',
            left: Math.max(20, Math.min((hole.x + hole.w / 2) - cardLeft - 8, cardW - 36)),
            ...(arrowAbove ? { top: -8 } : { bottom: -8 }),
            width: 16, height: 16, background: 'var(--paper)', transform: 'rotate(45deg)',
          }}/>
        )}
        <div style={{ position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: 'Geist Mono, monospace',
            fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}>
            <span>{String(step + 1).padStart(2, '0')} / {String(totalSteps).padStart(2, '0')}</span>
            <span>·</span>
            <span>Tutorial</span>
          </div>
          <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 26, lineHeight: 1.1, marginTop: 10, letterSpacing: '-0.01em' }}>
            {current.title}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink-2)', marginTop: 10 }}>
            {current.body}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 18 }}>
            {TUTORIAL_STEPS.map((_, i) => (
              <div key={i} style={{
                flex: i === step ? 2 : 1, height: 3, borderRadius: 2,
                background: i <= step ? 'var(--ember)' : 'var(--paper-3)',
                transition: 'flex 300ms, background 300ms',
              }}/>
            ))}
          </div>
          <div style={{
            marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          }}>
            <button onClick={back} disabled={step === 0} style={{
              padding: '10px 14px', background: 'transparent',
              color: step === 0 ? 'var(--ink-4)' : 'var(--ink-2)',
              fontSize: 13, fontWeight: 500, borderRadius: 999,
            }}>Voltar</button>
            <button onClick={next} style={{
              padding: '12px 20px', background: 'var(--ink)', color: 'var(--paper)',
              fontSize: 14, fontWeight: 500, borderRadius: 999,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {step >= totalSteps - 1 ? 'Começar a ler' : 'Próximo'}
              <Icon name="arrow-right" size={14} color="var(--paper)" stroke={2.2}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Achievement Modal (meta de livro / meta do dia / livro terminado)
   ───────────────────────────────────────────────────────────── */

export type AchievementKind = 'book' | 'day' | 'finished';

export interface AchievementPayload {
  kind: AchievementKind;
  book?: BookV2 | null;
  // pagesToday: no 'book', o que ela leu HOJE naquele livro; no 'day', no dia
  // inteiro (na unidade do livro registrado — unit). Nunca a meta.
  data?: { pagesToday?: number; books?: number; unit?: string };
}

function Rays({ visible, color, dark }: { visible: boolean; color: string; dark: boolean }) {
  return (
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      transform: `translate(-50%, -50%) scale(${visible ? 1 : 0.3}) rotate(${visible ? 0 : -40}deg)`,
      width: 640, height: 640, pointerEvents: 'none',
      opacity: visible ? (dark ? 0.12 : 0.08) : 0,
      transition: 'all 1400ms 100ms cubic-bezier(.2,.8,.2,1)',
      zIndex: 1,
    }}>
      <svg width="640" height="640" viewBox="0 0 640 640">
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i / 24) * 360;
          return (
            <line key={i} x1="320" y1="320"
              x2={320 + Math.cos(a * Math.PI / 180) * 320}
              y2={320 + Math.sin(a * Math.PI / 180) * 320}
              stroke={color} strokeWidth={i % 2 === 0 ? 2 : 0.8}
            />
          );
        })}
      </svg>
      <svg width="640" height="640" viewBox="0 0 640 640" style={{ position: 'absolute', inset: 0 }}>
        {[120, 180, 250, 320].map((r, i) => (
          <circle key={r} cx="320" cy="320" r={r} fill="none" stroke={color} strokeWidth="1" opacity={0.5 - i * 0.1}/>
        ))}
      </svg>
    </div>
  );
}

function Sigil({ kind, dark, accent }: { kind: string; dark: boolean; accent: string }) {
  const paperBg = dark ? 'rgba(245,241,234,0.06)' : 'var(--paper-2)';
  const strokeColor = dark ? 'var(--paper)' : 'var(--ink)';
  const size = 160;
  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      <div style={{
        position: 'absolute', inset: -14, borderRadius: '50%',
        background: `radial-gradient(circle, ${accent}22 0%, transparent 65%)`,
        animation: 'pulse-halo 3s ease-in-out infinite',
      }}/>
      <svg width={size} height={size} viewBox="0 0 160 160" style={{ position: 'absolute', inset: 0 }}>
        <circle cx="80" cy="80" r="74" fill={paperBg} stroke={accent} strokeWidth="1"/>
        {Array.from({ length: 60 }).map((_, i) => {
          const a = (i / 60) * 360;
          const long = i % 5 === 0;
          const r1 = 74 - (long ? 8 : 4);
          const r2 = 74 - 1;
          return (
            <line key={i}
              x1={80 + Math.cos(a * Math.PI / 180) * r1}
              y1={80 + Math.sin(a * Math.PI / 180) * r1}
              x2={80 + Math.cos(a * Math.PI / 180) * r2}
              y2={80 + Math.sin(a * Math.PI / 180) * r2}
              stroke={long ? strokeColor : accent}
              strokeWidth={long ? 1.2 : 0.6} opacity={long ? 1 : 0.5}
            />
          );
        })}
        <circle cx="80" cy="80" r="54" fill="none" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      </svg>
      <svg width={size} height={size} viewBox="0 0 160 160" style={{ position: 'absolute', inset: 0 }}>
        {kind === 'medal' && (
          <g>
            <path d="M80 40c12 12 22 24 22 40 0 12-10 22-22 22s-22-10-22-22c0-10 6-18 10-24 0 6 4 10 8 10-4-10 4-20 4-26z" fill={accent} opacity="0.9"/>
            <path d="M80 60c6 6 12 12 12 22 0 7-5 12-12 12s-12-5-12-12c0-6 4-10 8-14-2 6 2 8 4 8-2-6 0-10 0-16z" fill={dark ? 'var(--paper)' : '#FFE0B0'} opacity="0.95"/>
          </g>
        )}
        {kind === 'sun' && (
          <g>
            <circle cx="80" cy="80" r="22" fill={accent}/>
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i / 12) * 360;
              const r1 = 32, r2 = 44;
              return (
                <line key={i}
                  x1={80 + Math.cos(a * Math.PI / 180) * r1}
                  y1={80 + Math.sin(a * Math.PI / 180) * r1}
                  x2={80 + Math.cos(a * Math.PI / 180) * r2}
                  y2={80 + Math.sin(a * Math.PI / 180) * r2}
                  stroke={accent} strokeWidth={i % 2 === 0 ? 3 : 1.5} strokeLinecap="round"
                />
              );
            })}
          </g>
        )}
        {kind === 'laurel' && (
          <g>
            <path d="M80 50 L80 115" stroke={accent} strokeWidth="2"/>
            {[-1, 1].map(s => (
              <g key={s}>
                {[0, 1, 2, 3, 4].map(i => (
                  <ellipse key={i}
                    cx={80 + s * (8 + i * 2)}
                    cy={60 + i * 12}
                    rx={s * 8} ry="4"
                    fill="none" stroke={accent} strokeWidth="1.5"
                    transform={`rotate(${s * (30 - i * 4)} ${80 + s * (8 + i * 2)} ${60 + i * 12})`}
                  />
                ))}
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

function Confetti({ active, dark }: { active: boolean; dark: boolean }) {
  const pieces = 40;
  const colors = dark
    ? ['#C7531A', '#E07A3B', '#F5F1EA', '#F4B041']
    : ['#C7531A', '#E07A3B', '#6B3E5C', '#5E7156'];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }}>
      {active && Array.from({ length: pieces }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.2;
        const dur = 3 + Math.random() * 2;
        const rot = Math.random() * 720 - 360;
        const size = 4 + Math.random() * 6;
        const color = colors[i % colors.length];
        const drift = (Math.random() - 0.5) * 60;
        const shape = i % 3;
        return (
          <div key={i} style={{
            position: 'absolute', top: '-10%', left: `${left}%`,
            width: size, height: shape === 0 ? size : size * 2,
            background: color,
            borderRadius: shape === 2 ? '50%' : '1px',
            opacity: 0.85,
            animation: `confetti-fall ${dur}s ${delay}s ease-in forwards`,
            transform: `rotate(${rot}deg)`,
            ['--drift' as any]: `${drift}px`,
          } as React.CSSProperties}/>
        );
      })}
    </div>
  );
}

function StampBadge({ label, dark }: { label: string; dark: boolean }) {
  return (
    <div style={{
      padding: '10px 18px', borderRadius: 8,
      border: `1.5px solid ${dark ? 'rgba(245,241,234,0.3)' : 'var(--line-strong)'}`,
      display: 'inline-flex', alignItems: 'center', gap: 10,
      fontFamily: 'Geist Mono, monospace',
      fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
      color: dark ? 'rgba(245,241,234,0.7)' : 'var(--ink-3)',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)' }}/>
      {label}
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)' }}/>
    </div>
  );
}

export const AchievementModal: React.FC<{
  open: boolean;
  payload: AchievementPayload | null;
  onClose: () => void;
}> = ({ open, payload, onClose }) => {
  // O conteúdo entra junto com o rótulo (auditoria de 31/10, achado 5): antes
  // o ícone e o texto vinham em escada até 1,3 s, e a tela parecia vazia.
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (open) {
      setPhase(0);
      const t = setTimeout(() => setPhase(1), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open || !payload) return null;

  const { kind, book, data } = payload;
  // "1 página" / "40 páginas", na unidade do livro (capítulos, posições…).
  const contar = (n: number, unidade?: string) => { const u = unitMeta(unidade); return `${n} ${n === 1 ? u.one : u.long}`; };
  const lidasHoje = data?.pagesToday ?? 0;
  const cfg: Record<AchievementKind, any> = {
    book: {
      eyebrow: 'Meta do livro', title: 'Hábito cumprido',
      accent: 'var(--ember)', bg: 'var(--paper)', textColor: 'var(--ink)',
      subtitle: book ? `Hoje você leu ${contar(lidasHoje, (book as any).progressUnit)} de` : '',
      highlight: book?.title ?? '', sigilKind: 'medal', badge: 'ECHO · I',
    },
    day: {
      eyebrow: 'Meta do dia inteiro', title: 'O dia se completa',
      accent: 'var(--ember)', bg: '#1A1614', textColor: '#F5F1EA',
      subtitle: `${contar(lidasHoje, data?.unit)} ${unitMeta(data?.unit).unit === 'chapters' ? 'atravessado' : 'atravessada'}${lidasHoje === 1 ? '' : 's'}`,
      highlight: `em ${data?.books ?? 1} ${data?.books === 1 ? 'livro' : 'livros'}`,
      sigilKind: 'sun', badge: 'APOGEU · I',
    },
    finished: {
      eyebrow: 'Livro terminado', title: 'Capítulo final',
      accent: 'var(--ember)', bg: 'var(--paper)', textColor: 'var(--ink)',
      subtitle: 'Você chegou ao fim de',
      highlight: book?.title ?? '', sigilKind: 'laurel', badge: 'ÓPUS · COMPLETO',
    },
  };
  const config = cfg[kind];
  const isDark = kind === 'day';

  return (
    <div data-comemoracao={kind} style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: config.bg, color: config.textColor,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      maxWidth: 480, margin: '0 auto',
    }}>
      <Rays visible={phase >= 1} color={config.accent} dark={isDark}/>
      <Confetti active={phase >= 1} dark={isDark}/>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '40px 30px', position: 'relative', zIndex: 3,
      }}>
        <div className="mono" style={{
          fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: config.accent, opacity: phase >= 1 ? 1 : 0,
          transform: `translateY(${phase >= 1 ? 0 : 10}px)`,
          transition: 'all 500ms 80ms cubic-bezier(.2,.8,.2,1)',
        }}>✦ {config.eyebrow} ✦</div>

        <div style={{
          margin: '32px 0 28px',
          opacity: phase >= 1 ? 1 : 0,
          transform: `scale(${phase >= 1 ? 1 : 0.5}) rotate(${phase >= 1 ? 0 : -20}deg)`,
          transition: 'all 700ms 80ms cubic-bezier(.2,.9,.3,1.3)',
        }}>
          <Sigil kind={config.sigilKind} dark={isDark} accent={config.accent}/>
        </div>

        <div className="serif" style={{
          fontSize: 48, lineHeight: 1, letterSpacing: '-0.02em',
          textAlign: 'center', fontStyle: 'italic',
          opacity: phase >= 1 ? 1 : 0,
          transform: `translateY(${phase >= 1 ? 0 : 20}px)`,
          transition: 'all 500ms 80ms cubic-bezier(.2,.8,.2,1)',
        }}>{config.title}</div>

        <div style={{
          textAlign: 'center', marginTop: 18,
          opacity: phase >= 1 ? 1 : 0,
          transform: `translateY(${phase >= 1 ? 0 : 14}px)`,
          transition: 'all 500ms 80ms cubic-bezier(.2,.8,.2,1)',
        }}>
          <div data-comemoracao-sub style={{ fontSize: 15, color: isDark ? 'rgba(245,241,234,0.7)' : 'var(--ink-3)' }}>
            {config.subtitle}
          </div>
          {config.highlight && (
            <div className="serif" style={{
              fontSize: 24, lineHeight: 1.15, marginTop: 4,
              fontStyle: 'italic', color: config.accent,
            }}>"{config.highlight}"</div>
          )}
        </div>

        <div style={{
          marginTop: 44,
          opacity: phase >= 1 ? 1 : 0,
          transform: `scale(${phase >= 1 ? 1 : 0.9})`,
          transition: 'all 500ms 250ms cubic-bezier(.2,.9,.3,1.3)',
        }}>
          <StampBadge label={config.badge} dark={isDark}/>
        </div>
      </div>

      <div style={{
        padding: '0 24px 40px', position: 'relative', zIndex: 3,
        opacity: phase >= 1 ? 1 : 0, transition: 'opacity 300ms 300ms',
      }}>
        <button onClick={onClose} style={{
          width: '100%', background: config.accent, color: '#F5F1EA',
          padding: '16px', borderRadius: 18, fontSize: 15, fontWeight: 500,
        }}>Continuar lendo</button>
        <div style={{
          textAlign: 'center', marginTop: 14, fontSize: 11,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: isDark ? 'rgba(245,241,234,0.4)' : 'var(--ink-4)',
          fontFamily: 'Geist Mono, monospace',
        }}>
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
      </div>
    </div>
  );
};
